import { baseApiClient as api } from "../baseService";

export const addTagTypes = ["agent-threads"] as const;
const injectedRtkApi = api
  .enhanceEndpoints({
    addTagTypes,
  })
  .injectEndpoints({
    endpoints: (build) => ({
      listBulkAgentChatOperations: build.query<
        ListBulkAgentChatOperationsApiResponse,
        ListBulkAgentChatOperationsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/agent-threads/bulk/`,
          params: {
            limit: queryArg.limit,
            offset: queryArg.offset,
            ordering: queryArg.ordering,
            status: queryArg.status,
            created_by: queryArg.createdBy,
            firm: queryArg.firm,
            search: queryArg.search,
          },
        }),
        providesTags: ["agent-threads"],
      }),
      bulkCreateAgentChatThreads: build.mutation<
        BulkCreateAgentChatThreadsApiResponse,
        BulkCreateAgentChatThreadsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/agent-threads/bulk/`,
          method: "POST",
          body: queryArg.bulkCreateAgentChatThreadRequest,
        }),
        invalidatesTags: ["agent-threads"],
      }),
      getBulkAgentChatOperation: build.query<
        GetBulkAgentChatOperationApiResponse,
        GetBulkAgentChatOperationApiArg
      >({
        query: (queryArg) => ({
          url: `/api/agent-threads/bulk/${queryArg.id}/`,
        }),
        providesTags: ["agent-threads"],
      }),
    }),
    overrideExisting: false,
  });
export { injectedRtkApi as enhancedApi };
export type ListBulkAgentChatOperationsApiResponse =
  /** status 200  */ PaginatedBulkAgentChatOperationResponse;
export type ListBulkAgentChatOperationsApiArg = {
  limit?: number;
  offset?: number;
  ordering?: string;
  status?: string[];
  createdBy?: string[];
  firm?: string[];
  search?: string;
};
export type PaginatedBulkAgentChatOperationResponse = {
  count: number;
  next: string | null;
  previous: string | null;
  results: BulkAgentChatOperationSchema[];
};
export type BulkCreateAgentChatThreadsApiResponse =
  /** status 201  */ BulkCreateAgentChatThreadResponse;
export type BulkCreateAgentChatThreadsApiArg = {
  bulkCreateAgentChatThreadRequest: BulkCreateAgentChatThreadRequest;
};
export type GetBulkAgentChatOperationApiResponse =
  /** status 200  */ BulkAgentChatOperationDetailSchema;
export type GetBulkAgentChatOperationApiArg = {
  id: string;
};
export type BulkAgentChatOperationStatus =
  | "pending"
  | "running"
  | "completed"
  | "failed";
export type BulkAgentChatOperationSchema = {
  id: string | string;
  title: string;
  initial_task: string;
  status: BulkAgentChatOperationStatus;
  /** Total number of matters to process */
  total_count: number;
  /** Number of threads successfully created */
  created_count: number;
  /** Number of threads that failed to create */
  failed_count: number;
  /** List of error details for failed threads */
  error_details?: {
    [key: string]: string;
  }[];
  /** Display name of the user who created this operation */
  created_by_name?: string | null;
  /** Name of the firm associated with this operation */
  firm_name?: string | null;
  /** ID of the firm associated with this operation */
  firm_id?: string | null;
  /** When the operation was created (ISO 8601) */
  created_at: string;
  /** When the operation was last updated (ISO 8601) */
  modified_at: string;
  /** Push status of the linked FirmReadout record (pending/in_progress/completed/failed), or null if none */
  readout_push_status?: string | null;
  /** Notion page URL of the linked FirmReadout record, or null if none */
  readout_notion_url?: string | null;
};
export type BulkCreateAgentChatThreadResponse = {
  /** ID of the bulk operation to track progress */
  operation_id: string | string;
  /** Initial status of the operation */
  status: BulkAgentChatOperationStatus;
  /** Total number of matters to process */
  total_count: number;
};
export type ColumnDefinition = {
  /** Unique identifier for the column (e.g., 'client_name') */
  id: string;
  /** Description that tells the agent what this column represents */
  prompt: string;
};
export type TableDefinition = {
  columns: ColumnDefinition[];
};
export type BulkCreateAgentChatThreadRequest = {
  /** List of matter IDs to create threads for */
  matter_ids: (string | string)[];
  /** Title for all created threads */
  title: string;
  /** Initial task/prompt that will trigger the agent */
  initial_task: string;
  /** Optional table definition for structured data extraction */
  table_definition?: TableDefinition;
};
export type OrchestratorStatus =
  | "pending"
  | "running"
  | "completed"
  | "failed"
  | "unknown";
export type AgentChatThreadSchema = {
  id: string | string;
  title: string;
  matter_id: string | string;
  /** Name of the associated matter */
  matter_name?: string | null;
  /** Whether this is a system-created thread */
  system: boolean;
  /** Associated coverage ID, if any */
  coverage_id?: (string | string) | null;
  /** Bulk operation that created this thread, if any */
  bulk_operation_id?: (string | string) | null;
  /** Current status of the orchestrator workflow */
  orchestrator_status: OrchestratorStatus;
  /** Table definition for structured data extraction, if any */
  table_definition?: TableDefinition;
  /** Current table row data extracted by the agent, if any */
  current_table_row?: {
    [key: string]: string;
  } | null;
  /** When the thread was created (ISO 8601) */
  created_at: string;
};
export type BulkAgentChatOperationDetailSchema = {
  id: string | string;
  title: string;
  initial_task: string;
  status: BulkAgentChatOperationStatus;
  /** Total number of matters to process */
  total_count: number;
  /** Number of threads successfully created */
  created_count: number;
  /** Number of threads that failed to create */
  failed_count: number;
  /** List of error details for failed threads */
  error_details?: {
    [key: string]: string;
  }[];
  /** Threads created by this operation */
  threads?: AgentChatThreadSchema[];
  /** Notion page URL if this operation has been pushed */
  notion_page_url?: string | null;
  /** Status of the last Notion push (pending, in_progress, completed, failed) */
  notion_push_status?: string | null;
  /** Error message from the last failed Notion push */
  notion_push_error?: string | null;
  /** When the operation was created (ISO 8601) */
  created_at: string;
  /** When the operation was last updated (ISO 8601) */
  modified_at: string;
};
export const {
  useListBulkAgentChatOperationsQuery,
  useBulkCreateAgentChatThreadsMutation,
  useGetBulkAgentChatOperationQuery,
} = injectedRtkApi;
